import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Link, NoticeBox, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'

interface Props {
  newVersion?: boolean
}

const AddEmployeesNoticeBox = ({ newVersion = false }: Props) => {
  const [{ view }] = useSchedule()
  const [{ shiftsRows }] = useShiftsRows()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [showCallout, setShowCallout] = useState<boolean>(true)

  // We want this notice box to disappear once the user has added more employees to the table
  // that are not themselves.
  const {
    access: { id: currentUserId },
  } = useCurrentSession()

  // If there are 2 or more rows in the employees view only,
  // (shift rows in the work area view do not represent employees)
  // for sure they have added employees that are not themselves
  // so no need to do further checks
  if (view.isByEmployees && shiftsRows.length > 1) return null

  const employeesInShifts = new Set(shiftsRows.flatMap((row) => row.employees.map((e) => e.id)))

  // If we are in the work area view, and we have
  // more than 1 employee in shifts, it means I have added
  // other employees so no need to do further checks
  if (view.isByAreas && employeesInShifts.size > 1) return null

  const usersInShifts = new Set(
    employees
      .toArray()
      .filter((employee) => employeesInShifts.has(employee.id))
      .map((e) => employeeUser(e).id)
  )

  const isCurrentUserInTable = usersInShifts.has(currentUserId)

  // If I am the only one in the table it means
  // I have not added other employees yet
  const onlyCurrentUser = shiftsRows.length === 1 && isCurrentUserInTable

  if (!onlyCurrentUser) return null

  if (!showCallout) return null

  return (
    <>
      {newVersion ? (
        <Box paddingLeft="s40">
          <Callout
            type="info"
            title={i18n.t('shift_management.actions_sidepanel.add_employees_callout.title')}
            description={i18n.t(
              'shift_management.actions_sidepanel.add_employees_callout.description'
            )}
            link={
              <Link.Touchable
                onClick={() => navigate(navigation.employees.fullPath())}
                type="solid"
              >
                {i18n.t('shift_management.actions_sidepanel.add_employees_callout.link')}
              </Link.Touchable>
            }
            onRemove={() => setShowCallout(false)}
          />
        </Box>
      ) : (
        <NoticeBox
          borderRadius={{
            all: 'abs008',
          }}
          message={
            <Box gap="s8">
              <Text.Normal>{i18n.t('shift_management.import_employees.banner_copy')}</Text.Normal>
              <Link.Touchable onClick={() => navigate(navigation.employees.fullPath())}>
                {i18n.t('shift_management.empty_body.employees_section')}
              </Link.Touchable>
            </Box>
          }
        />
      )}
    </>
  )
}

export default observer(AddEmployeesNoticeBox)
